import { useMemo } from "react";

import { getCutoverDateForSchema, getOlderDataModule } from "../config/older-data-registry";
import { useStructuredSearchStore } from "../stores";
import {
  getStaticValueOptions,
  hasCutoverDateControl,
  hasFreeTextQueryControl,
  shouldSuppressValueDropdown,
} from "../config/schema-utils";
import {
  schemaHasAsyncSuggestions,
  getAggregationSuggestionField,
} from "../config/suggestion-config";
import {
  getFilterDefinitionByKey,
  getPromptFilterDefinition,
} from "../config/structured-search-schema";
import {
  getVersionedEntityConfig,
  isVersionedEntityField,
  VersionedEntityKind,
} from "../config/versioned-entity-config";
import {
  ANY_VERSION_LABEL,
  ANY_VERSION_VALUE,
  isStaleEntityListSuggestionOnVersionStep,
} from "../config/versioned-entity-utils";
import { STRUCTURED_SEARCH_QUERY_CONFIG } from "../config/query-config";
import { StructuredSearchField, StructuredSearchSuggestionField } from "../config/types";
import {
  fieldHasSuggestions,
  getNestedSuggestionKey,
  getSuggestionField,
  shouldSuggestNestedValues,
  shouldSuggestNestedKeys,
} from "../stores/structured-search/suggestion-utils";

import type {
  FieldSuggestionItem,
  OperatorSuggestionItem,
  PlainTextSearchSuggestionItem,
  SuggestionItem,
  ValueSuggestionItem,
} from "./types";
import { SuggestionStepEnum } from "./types";

import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useDebounce } from "@/hooks";
import { useGetSignals } from "@/queries/signals";
import { useStructuredSearchSuggestions } from "@/queries/structured-search";
import {
  formatSignalLabelValue,
  hydrateSignalKeySuggestions,
  SIGNAL_METADATA_KEY_PREFIX,
} from "@/routes/request-logs/search/signal-search-utils";

export const useStructuredSuggestions = (): {
  suggestions: SuggestionItem[];
  disabledFieldSuggestions: FieldSuggestionItem[];
  lockedFieldSuggestions: FieldSuggestionItem[];
  disabledOperatorSuggestions: OperatorSuggestionItem[];
  disabledPlainTextSearchSuggestion: PlainTextSearchSuggestionItem | null;
  unavailableSuggestionsReason: string;
  valueSuggestions: ValueSuggestionItem[];
  nestedKeyMode: boolean;
  isPromptField: boolean;
  isTraceVersionedEntityField: boolean;
  promptVersionStep: boolean;
  isLoadingSuggestions: boolean;
  isFetchingSuggestions: boolean;
  isAwaitingCurrentInputSuggestions: boolean;
} => {
  const store = useStructuredSearchStore();
  const { schema } = store;
  const olderData = getOlderDataModule(schema);
  const promptFilter = getPromptFilterDefinition(schema);
  const autocompleteEnabled = schemaHasAsyncSuggestions(schema);
  const { activeWorkspace, activeWorkspaceId } = useUser();
  const userToken = useUserToken();
  const nestedKeyMode = shouldSuggestNestedKeys(store);
  const nestedValueMode = shouldSuggestNestedValues(store);
  const metadataKey = getNestedSuggestionKey(store);
  const isSignalField = store.inProgress.field === StructuredSearchField.Signal;
  const isSignalKeyStep = isSignalField && nestedKeyMode;
  const isSignalValueStep = isSignalField && nestedValueMode;
  const isPromptField = promptFilter !== undefined && store.inProgress.field === promptFilter.key;
  const isTraceVersionedEntityField =
    isVersionedEntityField(schema, store.inProgress.field) && !isPromptField;
  const entitySelectStep = store.isVersionedEntitySelectStep;
  const entityVersionStep = store.isVersionedEntityVersionStep;
  const versionedEntityConfig = getVersionedEntityConfig(schema, store.inProgress.field);
  const inProgressEntityId = isPromptField ? store.inProgress.promptId : store.inProgress.entityId;
  const fieldSuggestions = store.fieldSuggestions as FieldSuggestionItem[];
  const lockedFieldSuggestions = useMemo(
    () =>
      store.hasLockedPromptFilter
        ? fieldSuggestions.filter(field => field.key === promptFilter?.key)
        : [],
    [fieldSuggestions, promptFilter?.key, store.hasLockedPromptFilter],
  );
  const availableFieldSuggestions = useMemo(
    () =>
      store.hasLockedPromptFilter
        ? fieldSuggestions.filter(field => field.key !== promptFilter?.key)
        : fieldSuggestions,
    [fieldSuggestions, promptFilter?.key, store.hasLockedPromptFilter],
  );
  const operatorSuggestions = store.operatorSuggestions as OperatorSuggestionItem[];
  const suppressDropdown = shouldSuppressValueDropdown(
    schema,
    store.inProgress.field,
    store.inProgress.operator,
  );
  const debouncedInputText = useDebounce(
    store.inputText,
    STRUCTURED_SEARCH_QUERY_CONFIG.SUGGESTION_DEBOUNCE_MS,
  );
  const isPostgresOnlyQuery =
    hasCutoverDateControl(schema) &&
    olderData.isPostgresOnlySearch({
      dateRange: store.dateRange,
      cutoverDate: getCutoverDateForSchema(schema, activeWorkspace),
    });
  const unavailableSuggestionsReason = olderData.OLDER_DATA_UNAVAILABLE_COPY;
  const structuredSearchPayload = useMemo(
    () =>
      schema.buildPayload({
        filters: store.filters.filter(filter => filter.id !== store.editingFilterId),
        dateRange: store.dateRange,
        q: store.q,
      }),
    [schema, store.editingFilterId, store.filters, store.dateRange, store.q],
  );

  const isKeyStep = store.step === "key";
  const requestPromptSelectStep = entitySelectStep && isPromptField;
  const traceEntitySelectStep = entitySelectStep && isTraceVersionedEntityField;
  const inProgressDefinition = getFilterDefinitionByKey(schema, store.inProgress.field);
  const traceEntitySuggestionField = inProgressDefinition
    ? getAggregationSuggestionField(inProgressDefinition)
    : "";
  const aggregationSuggestionsEnabled =
    autocompleteEnabled &&
    (store.step === SuggestionStepEnum.VALUE || (isKeyStep && nestedKeyMode)) &&
    !suppressDropdown &&
    !entitySelectStep &&
    (nestedKeyMode || nestedValueMode || entityVersionStep || fieldHasSuggestions(store));

  const registrySuggestionsEnabled =
    autocompleteEnabled &&
    store.step === SuggestionStepEnum.VALUE &&
    !suppressDropdown &&
    !entitySelectStep &&
    !aggregationSuggestionsEnabled;

  const promptSuggestionsQuery = useStructuredSearchSuggestions({
    schema,
    field: store.inProgress.field,
    input: store.inputText,
    enabled: autocompleteEnabled && requestPromptSelectStep && !suppressDropdown,
    suggestionField: StructuredSearchSuggestionField.Prompt,
    filterGroup: structuredSearchPayload,
    entitySelectStep: true,
  });

  const traceEntitySuggestionsQuery = useStructuredSearchSuggestions({
    schema,
    field: store.inProgress.field,
    input: store.inputText,
    enabled:
      autocompleteEnabled &&
      traceEntitySelectStep &&
      !suppressDropdown &&
      Boolean(traceEntitySuggestionField),
    suggestionField: traceEntitySuggestionField,
    filterGroup: structuredSearchPayload,
    entitySelectStep: false,
  });

  const valueSuggestionsQuery = useStructuredSearchSuggestions({
    schema,
    field: store.inProgress.field,
    input: store.inputText,
    enabled: aggregationSuggestionsEnabled,
    suggestionField: getSuggestionField(store),
    metadataKey,
    promptId:
      entityVersionStep && versionedEntityConfig?.kind === VersionedEntityKind.Prompt
        ? inProgressEntityId
        : undefined,
    workflowId:
      entityVersionStep && versionedEntityConfig?.kind === VersionedEntityKind.Workflow
        ? inProgressEntityId
        : undefined,
    filterGroup: structuredSearchPayload,
    entitySelectStep: false,
    entityVersionStep,
    hideSignalMetadata: isSignalKeyStep ? false : undefined,
    prefixOverride: isSignalKeyStep ? SIGNAL_METADATA_KEY_PREFIX : undefined,
  });

  const registrySuggestionsQuery = useStructuredSearchSuggestions({
    schema,
    field: store.inProgress.field,
    input: store.inputText,
    enabled: registrySuggestionsEnabled,
    suggestionField: getSuggestionField(store),
    filterGroup: structuredSearchPayload,
    entitySelectStep: false,
  });

  const signalsQuery = useGetSignals({
    userToken: userToken ?? "",
    workspaceId: activeWorkspaceId ?? 0,
    perPage: 100,
    enabled: isSignalField && aggregationSuggestionsEnabled,
  });

  const dynamicValueOptions = registrySuggestionsQuery.options;
  const dynamicValueLoading = registrySuggestionsQuery.isLoading;

  let valueSuggestionsSource = valueSuggestionsQuery.options;
  if (requestPromptSelectStep) {
    valueSuggestionsSource = promptSuggestionsQuery.options;
  } else if (traceEntitySelectStep) {
    valueSuggestionsSource = traceEntitySuggestionsQuery.options;
  }

  const signalNamesById = useMemo(() => {
    const names = new Map<number, string>();
    for (const signal of signalsQuery.data?.signals ?? []) {
      names.set(signal.id, signal.name);
    }
    return names;
  }, [signalsQuery.data?.signals]);

  const valueSuggestions = useMemo(() => {
    if (entityVersionStep && valueSuggestionsSource === null) {
      return [];
    }

    if (isSignalKeyStep) {
      if (valueSuggestionsSource === null || signalsQuery.isLoading) {
        return [];
      }
      return hydrateSignalKeySuggestions(
        valueSuggestionsSource,
        signalNamesById,
        store.inputText,
      ).map(item => ({
        value: item.value,
        label: item.label,
        count: item.count,
      }));
    }

    const mappedSuggestions = (valueSuggestionsSource ?? [])
      .map(item => {
        const label = isSignalValueStep
          ? (item.label ?? formatSignalLabelValue(item.value))
          : (item.label ?? undefined);
        return {
          value: item.value,
          count: item.count,
          prompt_name: item.prompt_name ?? undefined,
          label,
        };
      })
      .filter(
        item =>
          !isStaleEntityListSuggestionOnVersionStep(item, entityVersionStep, inProgressEntityId),
      );

    if (!entityVersionStep) {
      return mappedSuggestions;
    }

    return [
      {
        value: ANY_VERSION_VALUE,
        prompt_name: ANY_VERSION_LABEL,
        label: undefined,
      },
      ...mappedSuggestions,
    ];
  }, [
    entityVersionStep,
    inProgressEntityId,
    isSignalKeyStep,
    isSignalValueStep,
    signalNamesById,
    signalsQuery.isLoading,
    store.inputText,
    valueSuggestionsSource,
  ]);
  let isValueStepSuggestionsLoading = valueSuggestionsQuery.isLoading;
  if (requestPromptSelectStep) {
    isValueStepSuggestionsLoading = promptSuggestionsQuery.isLoading;
  } else if (traceEntitySelectStep) {
    isValueStepSuggestionsLoading = traceEntitySuggestionsQuery.isLoading;
  } else if (isSignalKeyStep) {
    isValueStepSuggestionsLoading =
      valueSuggestionsQuery.isLoading || signalsQuery.isLoading || signalsQuery.isFetching;
  }
  const isFetchingSuggestions =
    ((store.step === SuggestionStepEnum.VALUE || (isKeyStep && nestedKeyMode)) &&
      isValueStepSuggestionsLoading) ||
    (store.step === SuggestionStepEnum.VALUE && dynamicValueLoading);
  const isLoadingSuggestions =
    isFetchingSuggestions &&
    ((requestPromptSelectStep && promptSuggestionsQuery.options === null) ||
      (traceEntitySelectStep && traceEntitySuggestionsQuery.options === null) ||
      (isSignalKeyStep &&
        (valueSuggestionsQuery.options === null ||
          signalsQuery.isLoading ||
          signalsQuery.isFetching)) ||
      (!requestPromptSelectStep &&
        !traceEntitySelectStep &&
        !isSignalKeyStep &&
        valueSuggestionsQuery.options === null));
  const isAwaitingCurrentInputSuggestions =
    !isSignalKeyStep &&
    (store.step === SuggestionStepEnum.VALUE || (isKeyStep && nestedKeyMode)) &&
    store.inputText.trim().length > 0 &&
    (debouncedInputText !== store.inputText || isFetchingSuggestions);
  const plainTextSearchSuggestion = useMemo<PlainTextSearchSuggestionItem[]>(
    () =>
      autocompleteEnabled &&
      hasFreeTextQueryControl(schema) &&
      store.step === SuggestionStepEnum.FIELD &&
      store.inputText.trim().length > 0
        ? [
            {
              type: "plain_text_search",
              value: store.inputText.trim(),
              label: `Search for "${store.inputText.trim()}"`,
            },
          ]
        : [],
    [autocompleteEnabled, schema, store.inputText, store.step],
  );
  const disabledPlainTextSearchSuggestion =
    isPostgresOnlyQuery && plainTextSearchSuggestion.length > 0
      ? plainTextSearchSuggestion[0]
      : null;
  const partitionedFieldSuggestions = useMemo(
    () => olderData.partitionFieldsByOlderDataSupport(availableFieldSuggestions),
    [availableFieldSuggestions, olderData],
  );
  const disabledFieldSuggestions = [
    ...(isPostgresOnlyQuery ? partitionedFieldSuggestions.unsupported : []),
  ];
  const partitionedOperatorSuggestions = useMemo(() => {
    const allowedOperators = new Set(
      olderData.getOlderDataAllowedOperators(store.inProgress.field),
    );

    return operatorSuggestions.reduce(
      (result, operator) => {
        if (allowedOperators.has(operator.operator)) {
          result.supported.push(operator);
        } else {
          result.unsupported.push(operator);
        }
        return result;
      },
      {
        supported: [] as OperatorSuggestionItem[],
        unsupported: [] as OperatorSuggestionItem[],
      },
    );
  }, [olderData, operatorSuggestions, store.inProgress.field]);
  const disabledOperatorSuggestions =
    isPostgresOnlyQuery && store.step === SuggestionStepEnum.OPERATOR
      ? partitionedOperatorSuggestions.unsupported
      : [];

  const suggestions = useMemo<SuggestionItem[]>(() => {
    switch (store.step) {
      case SuggestionStepEnum.FIELD:
        if (!isPostgresOnlyQuery) {
          return [...plainTextSearchSuggestion, ...availableFieldSuggestions];
        }

        return partitionedFieldSuggestions.supported;

      case "key":
        return nestedKeyMode ? valueSuggestions : [];

      case SuggestionStepEnum.OPERATOR:
        if (!isPostgresOnlyQuery) {
          return operatorSuggestions;
        }

        return partitionedOperatorSuggestions.supported;

      case SuggestionStepEnum.VALUE:
        if (suppressDropdown) {
          return [];
        }

        {
          const definition = getFilterDefinitionByKey(schema, store.inProgress.field);
          const labeledAggregationSuggestions = valueSuggestions.map(suggestion => ({
            ...suggestion,
            label:
              definition?.options?.find(option => option.value === suggestion.value)?.label ??
              suggestion.label,
          }));
          const staticOptions = getStaticValueOptions(
            schema,
            store.inProgress.field,
            store.inputText,
          );
          if (registrySuggestionsEnabled && dynamicValueOptions !== null) {
            return dynamicValueOptions;
          }
          if (labeledAggregationSuggestions.length > 0) {
            return labeledAggregationSuggestions;
          }
          if (staticOptions) {
            return staticOptions;
          }
          return valueSuggestions;
        }

      default:
        return [];
    }
  }, [
    schema,
    dynamicValueOptions,
    isPostgresOnlyQuery,
    plainTextSearchSuggestion,
    nestedKeyMode,
    availableFieldSuggestions,
    operatorSuggestions,
    store.inProgress.field,
    store.inputText,
    partitionedFieldSuggestions.supported,
    partitionedOperatorSuggestions.supported,
    store.step,
    suppressDropdown,
    valueSuggestions,
    registrySuggestionsEnabled,
  ]);

  return {
    suggestions,
    disabledFieldSuggestions,
    lockedFieldSuggestions,
    disabledOperatorSuggestions,
    disabledPlainTextSearchSuggestion,
    unavailableSuggestionsReason,
    valueSuggestions,
    nestedKeyMode,
    isPromptField,
    isTraceVersionedEntityField,
    promptVersionStep: entityVersionStep,
    isLoadingSuggestions,
    isFetchingSuggestions,
    isAwaitingCurrentInputSuggestions,
  };
};
